import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import RestartIcon from '@clearkit/icons/glyphs/Restart';
import Trash from '@clearkit/icons/glyphs/Trash';
import { CKAttributeSelector, CKButton, CKSelect, CKSwitch } from 'clearkit';
import capitalize from 'lodash/capitalize';
import { useState } from 'react';

import {
  attributeForFinder,
  backendSupportedDataType,
  findAttribute,
  readableType,
  sourceIcon,
} from '~lib/dataEnrichment';
import { isTypeMismatch } from '~lib/isTypeMismatch';

import ServiceIcon from '../ServiceIcon';

export const RefreshButton = ({ onClickRefresh }) => {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);

    try {
      await onClickRefresh();
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };

  return (
    <CKButton
      data-testid="refreshPropertiesButton"
      isDisabled={loading}
      leftIcon={<RestartIcon />}
      onClick={onClick}
      variant="tertiary"
      variantColor="blue"
    >
      {loading ? 'Refreshing' : 'Refresh'}
    </CKButton>
  );
};

export default function FieldMappingRow({
  index,
  mapping,
  type,
  objectType,
  onChange,
  onClickRefreshAttributes,
  onRemove,
  sourceAttributes,
  targetAttributes,
  sourceErrors,
  targetErrors,
  showError,
  hasEnrichmentBetaUIFlag,
}) {
  const [overrideState, setOverrideState] = useState(
    !mapping.conditionalOverwrite ? 'Enabled' : 'Disabled',
  );
  const selectedSourceAttribute = sourceAttributes
    ? findAttribute(attributeForFinder(mapping, 'source'), sourceAttributes)
    : null;
  const selectedTargetAttribute = targetAttributes
    ? findAttribute(
        {
          value: mapping.targetField,
          objectType: objectType.name,
          sourceType: type,
        },
        targetAttributes,
      )
    : null;

  const inErrorState = (sourceOrTarget) => {
    if (sourceOrTarget === 'source') {
      return !!sourceErrors || (!mapping.sourceField && showError);
    } else if (sourceOrTarget === 'target') {
      return !!targetErrors || (!mapping.targetField && showError);
    } else return false;
  };

  const FieldOverwriteSelector = () => {
    const overrideOptions = [
      {
        value: 'Disabled',
        label: `Disabled`,
      },
      {
        value: 'Enabled',
        label: `Hard overwrite`,
      },
    ];

    return (
      <>
        {hasEnrichmentBetaUIFlag ? (
          <div className="w-full">
            <CKSelect
              items={overrideOptions.map((item) => ({
                label: item.label,
                value: item.value,
              }))}
              itemToString={(item) => (item ? item.label : '')}
              onSelectedItemChange={({ selectedItem }) => {
                if (selectedItem.value !== overrideState) {
                  setOverrideState(selectedItem.value);
                  onChange({
                    conditionalOverwrite: !mapping.conditionalOverwrite,
                  });
                }
              }}
              popoverPlacement="bottom-end"
              selectedItem={{
                label: overrideState,
                value: overrideState,
              }}
            />
          </div>
        ) : (
          <CKSwitch
            checked={!mapping.conditionalOverwrite}
            // See comment in FieldMappingForm. tl;dr if conditional overwrite is false, then
            // hard overwrite is enabled
            id={`mappings[${index}].overwrite`}
            onChange={() =>
              onChange({
                conditionalOverwrite: !mapping.conditionalOverwrite,
              })
            }
            srOnly={false}
          >
            {!mapping.conditionalOverwrite ? 'Enabled' : 'Disabled'}
          </CKSwitch>
        )}
      </>
    );
  };

  return (
    <div
      className={
        hasEnrichmentBetaUIFlag
          ? 'selective-enrichment-mapping-row'
          : 'mapping-row-grid'
      }
    >
      <CKAttributeSelector
        attributes={sourceAttributes}
        icon={<ServiceIcon type={selectedSourceAttribute} />}
        isTypeMismatch={(attribute) =>
          isTypeMismatch(attribute, selectedTargetAttribute)
        }
        onSelectedItemChange={(selected) => {
          onChange({
            sourceField: selected?.value,
            sourceObjectType: selected?.meta?.objectType,
            sourceType: selected?.meta?.sourceType,
          });
        }}
        placeholder="Find an attribute"
        selectedAttribute={selectedSourceAttribute || {}}
        variant={inErrorState('source') && 'error'}
        zIndex={30}
      />
      <DirectionForward className="text-gray" />
      <CKAttributeSelector
        attributes={targetAttributes}
        icon={sourceIcon(type)}
        isTypeMismatch={(attribute) =>
          isTypeMismatch(selectedSourceAttribute, attribute)
        }
        onSelectedItemChange={(selected) =>
          onChange({
            targetField: selected?.value,
            dataType: backendSupportedDataType(selected?.meta?.dataType),
          })
        }
        placeholder={`Choose a ${readableType(type)} ${capitalize(
          objectType.name,
        )} attribute`}
        rootCategoryIdentifier={`category_${type}`}
        rootCategoryLabel={readableType(type)} // here
        rootCategoryMeta={
          <RefreshButton onClickRefresh={onClickRefreshAttributes} />
        }
        selectedAttribute={selectedTargetAttribute || {}}
        variant={inErrorState('target') && 'error'}
        zIndex={30}
      />
      <div className="pl-4 border-l border-gray-600 border-opacity-10">
        <FieldOverwriteSelector />
      </div>
      <div className="text-right">
        <CKButton isSmall onClick={onRemove} variant="tertiary">
          <Trash className="text-gray-600" />
        </CKButton>
      </div>
    </div>
  );
}
